import React, { useState } from 'react';
import { FileText, CheckCircle, MessageCircle, Copy, RefreshCw, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';

interface EmailTemplateGeneratorProps {
  businessCategory: string;
  productDescription: string;
  onSelectTemplate: (template: string) => void;
  senderName: string;
  senderEmail: string;
}

export default function EmailTemplateGenerator({
  businessCategory,
  productDescription,
  onSelectTemplate,
  senderName,
  senderEmail
}: EmailTemplateGeneratorProps) {
  const [templates, setTemplates] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [enhancedLoading, setEnhancedLoading] = useState(false);
  const [showEnhanced, setShowEnhanced] = useState(false);

  const generateTemplates = () => {
    setLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      // Clean description by removing dashes and colons
      const cleanDescription = productDescription.replace(/[-:]/g, ' ').trim();
      
      // Custom subjects based on business category and description
      let subject1, subject2;
      
      // Generate subjects specific to QR code integration if mentioned
      if (cleanDescription.toLowerCase().includes('qr code')) {
        subject1 = `Transform Your ${businessCategory} Experience with QR Technology`;
        subject2 = `Seamless Customer Interactions: Next-Gen QR Solutions for ${businessCategory}`;
      } else {
        // Generic subjects for other products
        subject1 = `Revolutionize Your ${businessCategory} Business with Our Solution`;
        subject2 = `Boost Efficiency and Growth for Your ${businessCategory} Business`;
      }
      
      const templates = [
        // Template 1: Professional - Enhanced for sales intro
        `${senderName} <${senderEmail}>
Subject: ${subject1}

Hi {{recipient}},

Hope you're having a productive week. I noticed your company's impressive work in the ${businessCategory} space and thought our solution might align perfectly with your objectives.

${enhanceDescription(productDescription, "professional")}

What sets us apart:
• Proven 30% efficiency increase for similar businesses
• Easy implementation with seamless onboarding
• Dedicated support team with industry expertise

I'd value the opportunity to demonstrate how this could specifically benefit your operations. Would you be available for a brief 15-minute call next week?

Regards,
${senderName}
VantaPrints`,

        // Template 2: Casual/Friendly - Enhanced for sales intro
        `${senderName} <${senderEmail}>
Subject: ${subject2}

Hi {{recipient}},

I came across your company while researching leaders in the ${businessCategory} industry and was immediately impressed.

${enhanceDescription(productDescription, "casual")}

Our clients typically see:
→ 25-40% cost reduction
→ Implementation in less than 2 weeks
→ ROI within the first quarter

I'm sure you're busy, but I'd love to share how companies similar to yours are using our solution to drive impressive results. Would a quick 10-minute chat work for you?

Regards,
${senderName}
VantaPrints`
      ];
      
      setTemplates(templates);
      setLoading(false);
      
      // Select the first template by default
      if (templates.length > 0) {
        setSelectedIndex(0);
        onSelectTemplate(templates[0]);
      }
    }, 1500);
  };

  // Function to enhance description using simulated AI improvement
  function enhanceDescription(description: string, style: "professional" | "casual"): string {
    // Remove dashes and colons as requested
    const cleanDescription = description.replace(/[-:]/g, ' ').trim();
    
    // In a real implementation, this would call an AI API with the description
    // For demo purposes, we're creating compelling sales copy based on the description
    
    if (cleanDescription.length < 10) return cleanDescription;
    
    // Create tailored sales pitches based on the style
    if (style === "professional") {
      // For the example "Custom QR Code Integration for Seamless Customer Interactions, table numbering, payment and more"
      if (cleanDescription.toLowerCase().includes('qr code')) {
        return `We are excited to introduce our innovative solution tailored specifically for ${businessCategory} businesses. Our custom QR technology helps businesses streamline operations while boosting customer engagement through seamless interactions. This comprehensive platform handles everything from table management to payment processing and beyond, allowing your team to focus on delivering exceptional service rather than managing logistics.`;
      }
      
      return `We are excited to introduce our comprehensive solution tailored specifically for the ${businessCategory} sector. Our innovation addresses key pain points by streamlining workflows and enhancing operational efficiency. This proprietary system has consistently delivered measurable improvements for businesses like yours, with documented success across various performance metrics.`;
    } else {
      // For the example "Custom QR Code Integration for Seamless Customer Interactions, table numbering, payment and more"
      if (cleanDescription.toLowerCase().includes('qr code')) {
        return `We've built an amazing QR solution that's transforming how ${businessCategory} businesses operate! Our system makes customer interactions super smooth while simplifying table management, payments, and so much more. Your customers will love the convenience, and you'll love how it saves time and boosts your bottom line!`;
      }
      
      return `We've built something truly game changing for ${businessCategory} businesses! Our solution takes all the headaches out of your daily operations and turns them into opportunities for growth. It's super intuitive to use and starts delivering value from day one. Your team will wonder how they ever managed without it!`;
    }
  }

  const handleTemplateSelect = (index: number) => {
    setSelectedIndex(index);
    onSelectTemplate(templates[index]);
    toast.success("Template selected!");
  };

  const copyTemplate = (template: string) => {
    navigator.clipboard.writeText(template);
    toast.success("Template copied to clipboard!");
  };

  const enhanceTemplatesWithAI = () => {
    setEnhancedLoading(true);
    setShowEnhanced(true);
    
    // Simulate AI processing time
    setTimeout(() => {
      setEnhancedLoading(false);
      toast.success("Templates enhanced with AI sales optimization");
    }, 2000);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-lg font-semibold mb-4 flex items-center">
        <FileText className="h-5 w-5 mr-2 text-blue-500" />
        Email Template Generator
        {showEnhanced && <span className="ml-2 text-sm bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center">
          <Sparkles className="h-3 w-3 mr-1" /> AI Enhanced
        </span>}
      </h3>
      
      {templates.length === 0 ? (
        <div className="text-center py-6">
          <MessageCircle className="h-12 w-12 mx-auto text-blue-300 mb-3" />
          <p className="text-gray-600 mb-4">
            Generate professional sales email templates based on your business and product description
          </p>
          <button
            onClick={generateTemplates}
            disabled={loading || !businessCategory || !productDescription}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Generating...' : 'Generate Templates'}
          </button>
          {(!businessCategory || !productDescription) && (
            <p className="text-sm text-red-500 mt-2">
              Please fill in both business category and product description first
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="font-medium">Choose a template</h4>
            <div className="flex space-x-3">
              <button
                onClick={enhanceTemplatesWithAI}
                disabled={enhancedLoading || showEnhanced}
                className="text-sm flex items-center text-blue-500 hover:text-blue-700 disabled:opacity-50"
              >
                {enhancedLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> 
                    Enhancing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-1" /> 
                    {showEnhanced ? "AI Enhanced" : "Enhance with AI"}
                  </>
                )}
              </button>
              
              <button
                onClick={() => {
                  setTemplates([]);
                  setSelectedIndex(null);
                  setShowEnhanced(false);
                }}
                className="text-sm text-blue-500 hover:text-blue-700"
              >
                Generate New Templates
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            {templates.map((template, index) => {
              const lines = template.split('\n');
              const sender = lines[0];
              const subject = lines[1].replace('Subject: ', '');
              
              return (
                <div 
                  key={index}
                  className={`border rounded-md overflow-hidden ${
                    selectedIndex === index ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                  }`}
                >
                  <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium">Template {index + 1} {index === 0 ? "(Professional)" : "(Casual)"}</p>
                      <p className="text-xs text-gray-500">{subject}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyTemplate(template)}
                        className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
                        aria-label="Copy template"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleTemplateSelect(index)}
                        className={`p-1 ${
                          selectedIndex === index 
                            ? 'text-green-500' 
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        } rounded-full`}
                        aria-label={selectedIndex === index ? "Currently selected" : "Select this template"}
                      >
                        <CheckCircle className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-4 max-h-64 overflow-y-auto">
                    <div className="text-xs whitespace-pre-line">
                      {template.split('\n').slice(2).join('\n')}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 px-4 py-2 border-t">
                    <button
                      onClick={() => handleTemplateSelect(index)}
                      className={`w-full py-1 rounded-md text-sm ${
                        selectedIndex === index
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      {selectedIndex === index ? 'Currently Selected' : 'Use This Template'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}